import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Session timeout constants (in milliseconds)
const SESSION_CONFIG = {
  // Standard users: 14 days inactivity, 30 days absolute
  user: {
    inactivityTimeout: 14 * 24 * 60 * 60 * 1000, // 14 days
    absoluteExpiration: 30 * 24 * 60 * 60 * 1000, // 30 days
  },
  // Admins/Managers: 7 days inactivity, 30 days absolute
  admin: {
    inactivityTimeout: 7 * 24 * 60 * 60 * 1000, // 7 days
    absoluteExpiration: 30 * 24 * 60 * 60 * 1000, // 30 days
  },
};

const STORAGE_KEYS = {
  lastActivity: 'returnify_last_activity',
  sessionStart: 'returnify_session_start',
  userRole: 'returnify_user_role',
};

type UserRole = 'user' | 'admin' | 'manager' | 'support';

interface SessionGuardProps {
  children: React.ReactNode;
}

/**
 * SessionGuard component that wraps the app to:
 * 1. Track user activity
 * 2. Enforce session timeouts based on role
 * 3. Auto-logout on inactivity or absolute expiration
 */
export const SessionGuard = ({ children }: SessionGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoggingOutRef = useRef(false);

  // Get session config based on role
  const getSessionConfig = useCallback((role: UserRole) => {
    if (role === 'admin' || role === 'manager') {
      return SESSION_CONFIG.admin;
    }
    return SESSION_CONFIG.user;
  }, []);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    const sessionStart = localStorage.getItem(STORAGE_KEYS.sessionStart);
    // Only update if we have an active session
    if (sessionStart) {
      localStorage.setItem(STORAGE_KEYS.lastActivity, Date.now().toString());
    }
  }, []);

  // Clear session data
  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.lastActivity);
    localStorage.removeItem(STORAGE_KEYS.sessionStart);
    localStorage.removeItem(STORAGE_KEYS.userRole);
  }, []);

  // Force logout
  const forceLogout = useCallback(async (reason: string) => {
    // Prevent multiple logout attempts
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    clearSession();
    
    try {
      await supabase.auth.signOut();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error signing out:', error);
      }
    }
    
    toast.info('Session expired', {
      description: reason,
    });
    
    navigate('/auth');
    isLoggingOutRef.current = false;
  }, [clearSession, navigate]);

  // Check if session is still valid
  const checkSessionValidity = useCallback(async () => {
    const lastActivity = localStorage.getItem(STORAGE_KEYS.lastActivity);
    const sessionStart = localStorage.getItem(STORAGE_KEYS.sessionStart);
    const role = (localStorage.getItem(STORAGE_KEYS.userRole) as UserRole) || 'user';
    
    // If no session data, check if we have a Supabase session
    if (!lastActivity || !sessionStart) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Initialize session tracking if Supabase session exists but local tracking doesn't
        const now = Date.now().toString();
        localStorage.setItem(STORAGE_KEYS.sessionStart, now);
        localStorage.setItem(STORAGE_KEYS.lastActivity, now);
      }
      return;
    }
    
    const config = getSessionConfig(role);
    const now = Date.now();
    const lastActivityTime = parseInt(lastActivity, 10);
    const sessionStartTime = parseInt(sessionStart, 10);
    
    // Check absolute expiration
    if (now - sessionStartTime > config.absoluteExpiration) {
      await forceLogout('Your session has expired. Please sign in again.');
      return;
    }
    
    // Check inactivity timeout
    if (now - lastActivityTime > config.inactivityTimeout) {
      await forceLogout('You were signed out due to inactivity.');
      return;
    }
  }, [getSessionConfig, forceLogout]);

  // Set up activity listeners
  useEffect(() => {
    // Don't track on public pages
    const publicPaths = ['/', '/auth', '/terms'];
    const isPublicPage = publicPaths.includes(location.pathname);
    
    if (isPublicPage) {
      return;
    }

    const handleActivity = () => {
      updateActivity();
    };

    // Track user activity with throttling
    let lastUpdate = 0;
    const throttledActivity = () => {
      const now = Date.now();
      // Only update every 30 seconds to avoid excessive writes
      if (now - lastUpdate > 30000) {
        handleActivity();
        lastUpdate = now;
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, throttledActivity, { passive: true });
    });

    // Check session validity periodically (every 5 minutes)
    checkIntervalRef.current = setInterval(() => {
      checkSessionValidity();
    }, 5 * 60 * 1000);

    // Initial check
    checkSessionValidity();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledActivity);
      });
      
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [location.pathname, updateActivity, checkSessionValidity]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === 'SIGNED_OUT') {
          clearSession();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [clearSession]);

  return <>{children}</>;
};
